import {
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Tooltip,
  Select,
  MenuItem,
  Autocomplete
} from '@mui/material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import React, { useRef, useState } from 'react';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import logoImage from './thumbnail_TechForGov-v3.png';
import { Fields } from '../FormikFormLibrary/FormikTypes';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';

interface PdfGeneratorProps {
  allFields: any;
  PermitName: string;
}

const PdfGenerator: React.FC<PdfGeneratorProps> = ({
  allFields,
  PermitName
}) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const themeLogo = useSelector((store: RootState) => store.theme.themeLogo);
  let path = null;
  if (themeLogo) path = new URL(themeLogo).pathname;  const [isGenerating, setIsGenerating] = useState(false);

  const downloadPdf = async () => {
    if (!componentRef.current) return;

    setIsGenerating(true);
    try {
      const element = componentRef.current;
      element.style.cssText = `
        position: absolute;
        top: -9999px;
        left: -9999px;
      `;

      const canvas = await html2canvas(element, {
        scrollY: -window.scrollY,
        useCORS: true
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const aspectRatio = imgWidth / imgHeight;
      const scaledImgHeight = pdfWidth / aspectRatio;

      let y = 0;
      while (y < imgHeight) {
        const croppedCanvas = document.createElement('canvas');
        const croppedContext = croppedCanvas.getContext('2d');
        croppedCanvas.width = canvas.width;
        croppedCanvas.height = Math.min(
          canvas.height - y,
          scaledImgHeight * (canvas.width / pdfWidth)
        );

        croppedContext?.drawImage(
          canvas,
          0,
          y,
          canvas.width,
          croppedCanvas.height,
          0,
          0,
          croppedCanvas.width,
          croppedCanvas.height
        );

        const croppedImgData = croppedCanvas.toDataURL('image/png');

        pdf.addImage(
          croppedImgData,
          'PNG',
          0,
          0,
          pdfWidth,
          (croppedCanvas.height / croppedCanvas.width) * pdfWidth
        );

        y += pdfHeight * (imgHeight / scaledImgHeight);

        if (y < imgHeight) {
          pdf.addPage();
        }
      }

      pdf.save('download.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const formateDateToDispaly = (date: string) => {
    if (date) {
      const dateStr = date.split('T')[0];
      if (dateStr.startsWith('1970-01-01')) return '';
      const [year, month, day] = dateStr.split('-');
      return `${month}/${day}/${year}`;
    }
    return '';
  };

  const renderField = (item: Fields) => {
    const commonProps = {
      inputProps: {
        style: {
          fontSize: 16,
          fontWeight: 'normal',
          color: '#555'
        }
      },
      InputLabelProps: {
        style: {
          fontSize: 16,
          fontWeight: 'normal',
          color: '#555'
        }
      },
      sx: {
        flex: 1,
        width: '100%',
        minWidth: '250px'
      },
      color: "secondary" as const,
      variant: "standard" as const,
      disabled: item.disabled,
      value: item.fieldType === 'date' ? formateDateToDispaly(typeof item?.initialValue === 'string' ? item.initialValue : '') : (typeof item?.initialValue === 'string' ? item.initialValue : String(item?.initialValue ?? '')),
      focused: !item.disabled
    };

    switch (item.fieldType) {
      case 'select':
        return (
          <FormControl sx={{ flex: 1 }}>
            <Select {...commonProps}>
              {Array.isArray(item.options) && item.options.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'autocomplete':
        return (
          <Autocomplete
            disabled={item.disabled}
            options={item.options || []}
            getOptionLabel={(option: any) => option.label || option.BlockLot || ''}
            value={item.initialValue ? { label: item.initialValue } : null}
            renderInput={(params) => (
              <TextField
                {...params}
                {...commonProps}
                placeholder={item.placeHolder}
              />
            )}
            sx={{ flex: 1 }}
          />
        );

      case 'radio':
        return (
          <FormControl sx={{ flex: 1 }}>
            <RadioGroup
              aria-labelledby="radio-buttons-group-label"
              name="radio-buttons-group"
              sx={{ flexDirection: 'row', gap: 2 }}
            >
              {item?.options?.map((option: any) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={
                    <Radio
                      sx={{
                        color: '#666',
                        '&.Mui-checked': {
                          color: '#1976d2'
                        }
                      }}
                      checked={item.initialValue === option.value}
                      disabled={item.disabled}
                    />
                  }
                  label={
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 14,
                        color: '#555'
                      }}
                    >
                      {option.label}
                    </Typography>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case 'mobile':
      case 'number':
      case 'email':
      case 'text':
      case 'note':
      case 'date':
      default:
        return <TextField {...commonProps} type={item.fieldType === 'mobile' ? 'tel' : item.fieldType === 'date' ? 'text' : item.fieldType} />;
    }
  };

  return (
    <>
      <div className="no-break">
        <Box
          ref={componentRef}
          sx={{
            position: isGenerating ? 'static' : 'absolute',
            top: isGenerating ? 'auto' : '-9999px',
            left: isGenerating ? 'auto' : '-9999px'
          }}
        >
          <Box
            sx={{
              padding: '20px',
              borderBottom: '2px solid #ddd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px',
              color: '#fff'
            }}
          >
            <Box>
              <img width={400} src={path || logoImage} alt="logo" />
            </Box>
            <Typography
              variant="h2"
              align="center"
              sx={{ fontWeight: 'bold', color: 'black' }}
            >
              Application For {PermitName}
            </Typography>
          </Box>

          {allFields?.map((item: Fields, index: number) => {
            if (item.fieldType !== 'file')
              return (
                <Box
                  key={item.fieldName}
                  className="no-break"
                  m={2}
                  sx={{
                    display: 'flex',
                    gap: 3,
                    flexDirection: 'row',
                    padding: '20px',
                    alignItems: 'center',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: '#f9f9f9',
                    marginBottom: '10px'
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      width: '10%',
                      fontWeight: 'bold',
                      fontSize: 18,
                      color: '#333'
                    }}
                  >
                    {index + 1}.
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      width: '30%',
                      fontWeight: 'bold',
                      fontSize: 16,
                      color: '#333'
                    }}
                  >
                    {item.fieldLabel}:
                  </Typography>
                  {renderField(item)}
                </Box>
              );
          })}

          <Box m={4}>
            <FormControlLabel
              control={<Checkbox size="large" name="agreement" />}
              label={
                <Typography variant="h6" m={2} sx={{ color: '#333' }}>
                  The applicant agrees to comply with all applicable federal,
                  state, and local laws, regulations, and ordinances while
                  performing the permitted activities.
                </Typography>
              }
            />
          </Box>

          <Box
            m={5}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              gap: 5,
              borderTop: '2px solid #ddd',
              paddingTop: 20,
              marginTop: 20
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                mb={2}
                variant="h6"
                sx={{ fontWeight: 'bold', color: '#333' }}
              >
                Manager/Approver Signature
              </Typography>
              <TextField
                fullWidth
                color="secondary"
                variant="standard"
                focused
                sx={{ minWidth: '300px' }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                mb={2}
                variant="h6"
                sx={{ fontWeight: 'bold', color: '#333' }}
              >
                Applicant Signature
              </Typography>
              <TextField
                fullWidth
                sx={{ minWidth: '300px' }}
                color="secondary"
                variant="standard"
                focused
              />
            </Box>
          </Box>
        </Box>

        <Tooltip title="Download Application">
          <IconButton size="large" color="primary" onClick={downloadPdf}>
            <DownloadForOfflineIcon />
          </IconButton>
        </Tooltip>
      </div>
    </>
  );
};

export default PdfGenerator;